import { useCallback } from "react";

import { uniqBy } from "lodash";

import { usePersistedState } from "src/hooks/use-persisted-state";

// We persist the workspace metadata (rather than just the id) so the
// "Switch workspace" menu can render recents without resolving them against a
// workspace list. Resolution would otherwise drop workspaces the current user
// is not a member of — e.g. Hightouch admins who switch into customer
// workspaces across organizations. The org name and member count mirror what
// the menu's search results show. They are optional so entries persisted by
// older builds still render (just without that extra metadata).
export type RecentWorkspace = {
  id: string;
  name: string;
  slug: string | null;
  organizationName?: string | null;
  memberCount?: number;
};

// Recents span across workspaces, so this key is intentionally global (per
// browser/user) rather than namespaced by the active workspace id.
const RECENT_WORKSPACES_KEY = "recent-workspaces";

// Store a few more than we display so that, after excluding the active
// workspace (which is always the most recent), we can still surface a full
// set of recents.
const MAX_STORED_RECENT_WORKSPACES = 10;

const isRecentWorkspace = (value: unknown): value is RecentWorkspace =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RecentWorkspace).id === "string" &&
  typeof (value as RecentWorkspace).name === "string";

export const useRecentWorkspaces = () => {
  const { value, set } = usePersistedState<RecentWorkspace[]>(
    RECENT_WORKSPACES_KEY,
    [],
  );

  // Drop legacy/malformed entries — the key previously stored bare id strings,
  // which can no longer be rendered without a name.
  const recentWorkspaces = (value ?? []).filter(isRecentWorkspace);

  const addRecentWorkspace = useCallback(
    (workspace: RecentWorkspace) => {
      set(
        uniqBy([workspace, ...recentWorkspaces], (w) => w.id).slice(
          0,
          MAX_STORED_RECENT_WORKSPACES,
        ),
      );
    },
    [set, recentWorkspaces],
  );

  return { recentWorkspaces, addRecentWorkspace };
};
